import React, {useMemo} from "react";
import Form from "../../../../../../components/base/form";
import ValidateMessages from "../../../../../../../core/models/static/texts/vallidate-messages";
import useAccessLevels from "../../../../../../hooks/use-access-levels";
import {MuiRFF, Yup} from "../../../../../../components/form";
import PriceLevelsSection from "./price-levels";
import QuoteBypassSection from "./quote-bypass";
import SearchLimitsSection from "./search-limits";
import PaymentSettingsSection from "./payment-settings";
import Grid2 from "@mui/material/Unstable_Grid2";
import OrderSettingsSection from "./order-settings";


export const priceLevelIdentifier = 'price-level-';
export const newPriceLevelCategoryIdentifier = 'new-category';
export const newPriceLevelIdentifier = 'new-price-level';

const CustomerSettingsSection = ({
                                     customer,
                                     setCustomer,
                                     bypass,
                                     setBypass,
                                     territory,
                                     setTerritory,
                                     accountManager,
                                     setAccountManager,
                                     formData,
                                     refetchAccounting,
                                     deleting,
                                 }) => {
    const operatorAccessLevels = useAccessLevels();

    const schema = useMemo(() => {
        const res = Yup.object().shape({
            priceLevel: Yup.mixed().nullable().required(ValidateMessages.required),
            paymentTerms: Yup.mixed().nullable().required(ValidateMessages.required),
            creditLimit: Yup.string().nullable().required(ValidateMessages.required),
            searchLimitType: Yup.mixed().nullable(),
            searchLimitCount: Yup.string().nullable(),
            periodDays: Yup.number().nullable().typeError(ValidateMessages.incorrectType('Number')),
            permittedTimes: Yup.number().nullable().typeError(ValidateMessages.incorrectType('Number')),
            orderSettingsTerritory: Yup.mixed().nullable(),
            orderSettingsAccountManager: Yup.mixed().nullable(),
        })
        customer?.priceLevels?.forEach(e => {
            res[`${priceLevelIdentifier}${e.id}`] = Yup.mixed().nullable();
        });
        res[newPriceLevelIdentifier] = Yup.mixed().nullable();
        res[newPriceLevelCategoryIdentifier] = Yup.mixed().nullable();
        return res;
    }, [customer?.priceLevels])

    const initialValues = useMemo(() => {
        const priceLevelEntry = customer?.priceLevels?.find(e => !e.partCategoryId);
        const res = {
            paymentTerms: customer?.paymentTerms,
            priceLevel: priceLevelEntry ? formData.priceLevels.find(e => e.id === priceLevelEntry.priceLevelId) : null,
            creditLimit: customer?.creditLimit,
            searchLimitType: customer?.searchLimitations ? {
                id: customer?.searchLimitations.periodTypeId,
                name: customer?.searchLimitations.periodTypeTitle,
            } : null,
            searchLimitCount: customer?.searchLimitations ? customer?.searchLimitations.limit : null,
            periodDays: bypass?.periodDays,
            permittedTimes: bypass?.permittedTimes,
            orderSettingsTerritory: territory,
            orderSettingsAccountManager: accountManager,
        };
        customer?.priceLevels?.filter(e => !!e.partCategoryId)?.forEach(e => {
            res[`${priceLevelIdentifier}${e.priceLevelId}`] = formData.priceLevels.find(p => p.id === e.priceLevelId) ?? null;
        });
        res[newPriceLevelIdentifier] = '';
        res[newPriceLevelCategoryIdentifier] = '';
        return res;
    }, [accountManager, bypass?.periodDays, bypass?.permittedTimes, customer?.creditLimit, customer?.paymentTerms, customer?.priceLevels, customer?.searchLimitations, formData.priceLevels, territory])

    const validate = MuiRFF.makeValidate(schema);
    const required = MuiRFF.makeRequired(schema);

    return (
        <>
            <Form
                onSubmit={() => undefined}
                validate={validate}
                initialValues={initialValues}
                render={({submitting, values, initialValues, form}) => (
                    <Grid2 container spacing={2}>
                        <Grid2 xs={12}>
                            <PaymentSettingsSection
                                customer={customer}
                                setCustomer={setCustomer}
                                values={values}
                                initialValues={initialValues}
                                form={form}
                                formData={formData}
                                required={required}
                                deleting={deleting}
                                submitting={submitting}
                                refetchAccounting={refetchAccounting}
                            />
                        </Grid2>
                        {
                            operatorAccessLevels.packages.base.manageTerritories &&
                            <Grid2 xs={12}>
                                <OrderSettingsSection
                                    initialValues={initialValues}
                                    values={values}
                                    customerId={customer?.id}
                                    required={required}
                                    form={form}
                                    submitting={submitting}
                                    setBypass={setBypass}
                                    deleting={deleting}
                                    setTerritory={setTerritory}
                                    setAccountManager={setAccountManager}
                                />
                            </Grid2>
                        }
                        <Grid2 xs={12}>
                            <PriceLevelsSection
                                initialValues={initialValues}
                                values={values}
                                required={required}
                                form={form}
                                submitting={submitting}
                                priceLevels={formData.priceLevels}
                                customer={customer}
                                setCustomer={setCustomer}
                                refetchAccounting={refetchAccounting}
                                deleting={deleting}
                            />
                        </Grid2>
                        <Grid2 xs={12}>
                            <SearchLimitsSection
                                customer={customer}
                                setCustomer={setCustomer}
                                values={values}
                                initialValues={initialValues}
                                form={form}
                                formData={formData}
                                required={required}
                                deleting={deleting}
                                submitting={submitting}
                            />
                        </Grid2>
                        {
                            operatorAccessLevels.packages.sales.quoteBypass &&
                            <Grid2 xs={12}>
                                <QuoteBypassSection
                                    initialValues={initialValues}
                                    values={values}
                                    customerId={customer?.id}
                                    required={required}
                                    form={form}
                                    submitting={submitting}
                                    setBypass={setBypass}
                                    deleting={deleting}
                                />
                            </Grid2>
                        }
                    </Grid2>
                )}
            />
        </>
    );
}


export default CustomerSettingsSection;
